import { zodResolver } from '@hookform/resolvers/zod';
import flow from 'lodash/flow';
import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import { useApiClient } from '@eluve/api-client-provider';
import { cacheUtils } from '@eluve/apollo-client';
import {
  Button,
  Combobox,
  ComboboxDropdown,
  ComboboxOption,
  ComboboxSelectButton,
  ComboboxSelectCheck,
  Dialog,
  DialogContent,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Heading,
  Icon,
  Input,
  Stack,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@eluve/components';
import { externalEhrsFragment } from '@eluve/frontend-feature-location-hierarchy';
import { SupportedVendors, VendorTypesLookup } from '@eluve/graphql-types';
import { useTranslation } from '@eluve/i18n';
import { getVendorDataMapper } from '@eluve/vendor';

interface NewEhrFormProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}

const baseFormSchema = z.object({
  vendor: z.nativeEnum(VendorTypesLookup),
  domain: z
    .string()
    .min(1)
    .transform((val) => {
      const domain = val
        .replace(/^(https?:\/\/)?(www\.)?/i, '')
        .replace(/\/$/, '');
      return domain;
    }),
});

export const NewEhrForm: React.FC<NewEhrFormProps> = ({
  open,
  onOpenChange,
}) => {
  const toast = useToast();
  const { t } = useTranslation();
  const apiClient = useApiClient();
  const [isCreating, setIsCreating] = useState(false);

  const { tenantId, locationId } = useParams() as {
    tenantId: string;
    locationId: string;
  };

  const [selectedVendor, setSelectedVendor] = useState<
    SupportedVendors | undefined
  >(undefined);

  const vendorProvider = selectedVendor
    ? getVendorDataMapper(selectedVendor)
    : undefined;

  const dynamicSchema = useMemo(() => {
    if (!vendorProvider?.getVendorSchema) {
      return baseFormSchema;
    }
    const vendorSchema = vendorProvider.getVendorSchema();
    const extendSchema = flow(
      (schema: z.ZodObject<z.ZodRawShape>) =>
        vendorSchema.secretSchema
          ? schema.extend(vendorSchema.secretSchema.shape)
          : schema,
      (schema: z.ZodObject<z.ZodRawShape>) =>
        vendorSchema.plainTextSchema
          ? schema.extend(vendorSchema.plainTextSchema.shape)
          : schema,
    );

    return extendSchema(baseFormSchema);
  }, [vendorProvider]);

  type DynamicSchemaType = z.infer<typeof dynamicSchema>;

  const newEhrForm = useForm<DynamicSchemaType>({
    resolver: zodResolver(dynamicSchema),
    defaultValues: {
      vendor: undefined,
      domain: '',
    } as Partial<DynamicSchemaType>,
  });

  const categorizedFields = useMemo(() => {
    const shape = dynamicSchema.shape;
    const allFields = Object.keys(shape).filter(
      (field) => field !== 'vendor' && field !== 'domain',
    );

    const secretFields: string[] = [];
    const plainTextFields: string[] = [];

    if (vendorProvider?.getVendorSchema) {
      const vendorSchema = vendorProvider.getVendorSchema();

      if (vendorSchema.secretSchema) {
        secretFields.push(...Object.keys(vendorSchema.secretSchema.shape));
      }

      if (vendorSchema.plainTextSchema) {
        plainTextFields.push(
          ...Object.keys(vendorSchema.plainTextSchema.shape),
        );
      }
    }

    return {
      all: allFields,
      secret: secretFields,
      plainText: plainTextFields,
    };
  }, [dynamicSchema, vendorProvider]);

  const onSubmitNewEhr = async (data: DynamicSchemaType) => {
    const { vendor, domain, ...vendorConfiguration } = data;

    const secretConfig: Record<string, string> = {};
    const plainTextConfig: Record<string, string> = {};

    categorizedFields.secret.forEach((field) => {
      if (field in vendorConfiguration) {
        secretConfig[field] = vendorConfiguration[field] as string;
      }
    });

    categorizedFields.plainText.forEach((field) => {
      if (field in vendorConfiguration) {
        plainTextConfig[field] = vendorConfiguration[field] as string;
      }
    });

    try {
      setIsCreating(true);
      const response = await apiClient.locations.addEhr({
        body: {
          vendor,
          domain,
          vendorConfiguration: {
            secretConfig,
            plainTextConfig,
          },
        },
        params: { tenantId, locationId },
      });

      if (response.status !== 201) {
        toast.error(t('tenantAdmin.location.detailsUpdateFailed'));
        return;
      }
      const { externalEhrId } = response.body;

      cacheUtils.updateFragment(
        {
          fragment: externalEhrsFragment,
          key: { id: tenantId },
        },
        (existing) => {
          if (
            !existing ||
            existing.externalEhrs.find((ehr) => ehr.id === externalEhrId)
          ) {
            return existing;
          }
          return {
            __typename: 'Tenants' as const,
            id: tenantId,
            externalEhrs: [
              ...(existing.externalEhrs || []),
              {
                domain,
                vendor,
                id: externalEhrId,
                __typename: 'ExternalEhr' as const,
              },
            ],
          };
        },
      );

      newEhrForm.reset({
        vendor: undefined,
        domain: '',
      });
      onOpenChange(false);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          size="lg"
          className="flex flex-col items-center justify-center gap-5 p-10"
        >
          <Stack align="stretch" gap={2}>
            <Heading size="3xl">{t('tenantAdmin.newEhr.title')}</Heading>

            <Form {...newEhrForm}>
              <form onSubmit={newEhrForm.handleSubmit(onSubmitNewEhr)}>
                <Stack gap={2} align="stretch">
                  <FormField
                    control={newEhrForm.control}
                    name="vendor"
                    render={({ field, fieldState: { error } }) => (
                      <FormItem>
                        <FormLabel>
                          {t('tenantAdmin.newEhr.vendorLabel')}
                        </FormLabel>
                        <Combobox>
                          <ComboboxSelectButton className="w-full border-transparent hover:bg-bg-quaternary">
                            <Stack gap={2} direction="horizontal">
                              {field.value ??
                                t('tenantAdmin.newEhr.selectVendor')}
                            </Stack>
                          </ComboboxSelectButton>
                          <ComboboxDropdown
                            searchPlaceholder={t(
                              'tenantAdmin.newEhr.selectVendor',
                            )}
                          >
                            {(
                              Object.keys(
                                VendorTypesLookup,
                              ) as SupportedVendors[]
                            ).map((vendor) => (
                              <ComboboxOption
                                key={vendor}
                                onSelect={() => {
                                  field.onChange(vendor);
                                  setSelectedVendor(vendor);

                                  const mapper = getVendorDataMapper(vendor);
                                  if (mapper?.getVendorSchema) {
                                    const vendorSchema =
                                      mapper.getVendorSchema();
                                    const applyDefaults = (
                                      schema?: z.ZodObject<z.ZodRawShape>,
                                    ) => {
                                      if (!schema) return;
                                      for (const [
                                        key,
                                        fieldSchema,
                                      ] of Object.entries(schema.shape)) {
                                        const zodField =
                                          fieldSchema as z.ZodTypeAny;
                                        if (
                                          zodField._def.typeName ===
                                          'ZodDefault'
                                        ) {
                                          newEhrForm.setValue(
                                            key,
                                            zodField._def.defaultValue(),
                                          );
                                        }
                                      }
                                    };
                                    applyDefaults(vendorSchema.secretSchema);
                                    applyDefaults(vendorSchema.plainTextSchema);
                                  }
                                }}
                              >
                                <ComboboxSelectCheck
                                  className="mr-2"
                                  selected={field.value === vendor}
                                />
                                {vendor}
                              </ComboboxOption>
                            ))}
                          </ComboboxDropdown>
                        </Combobox>
                        {error && (
                          <FormMessage className="mt-4">
                            {error.message}
                          </FormMessage>
                        )}
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={newEhrForm.control}
                    name="domain"
                    render={({ field, fieldState: { error } }) => (
                      <FormItem>
                        <FormLabel>
                          {t('tenantAdmin.newEhr.domainLabel')}
                        </FormLabel>
                        <Input
                          placeholder={t(
                            'tenantAdmin.newEhr.domainPlaceholder',
                          )}
                          {...field}
                        />
                        {error && <FormMessage>{error.message}</FormMessage>}
                      </FormItem>
                    )}
                  />
                  {selectedVendor && (
                    <>
                      {categorizedFields.secret.length > 0 && (
                        <Stack gap={2}>
                          <FormLabel>
                            <Heading size="lg" weight="semibold">
                              {t('tenantAdmin.newEhr.secretConfiguration')}
                            </Heading>
                          </FormLabel>
                          {categorizedFields.secret.map((field) => (
                            <FormField
                              key={field}
                              control={newEhrForm.control}
                              name={field}
                              render={({ field, fieldState: { error } }) => (
                                <FormItem>
                                  <FormLabel>
                                    <Stack gap={2} direction="horizontal">
                                      {field.name}
                                      {dynamicSchema.shape &&
                                        dynamicSchema.shape[field.name]
                                          ?.description && (
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Icon
                                                name="HelpCircle"
                                                size="sm"
                                              />
                                            </TooltipTrigger>
                                            <TooltipContent
                                              align="start"
                                              alignOffset={-50}
                                              className="max-w-60 border-none bg-white text-wrap text-text-secondary shadow-md"
                                            >
                                              {dynamicSchema.shape[field.name]
                                                ?.description ?? ''}
                                            </TooltipContent>
                                          </Tooltip>
                                        )}
                                    </Stack>
                                  </FormLabel>
                                  <Input
                                    type="password"
                                    placeholder={t(
                                      'tenantAdmin.newEhr.enterField',
                                      { field: field.name },
                                    )}
                                    {...field}
                                  />
                                  {error && (
                                    <FormMessage>{error.message}</FormMessage>
                                  )}
                                </FormItem>
                              )}
                            />
                          ))}
                        </Stack>
                      )}

                      {categorizedFields.plainText.length > 0 && (
                        <>
                          <FormLabel>
                            <Heading size="lg" weight="semibold">
                              {t('tenantAdmin.newEhr.plaintextConfiguration')}
                            </Heading>
                          </FormLabel>
                          {categorizedFields.plainText.map((field) => (
                            <FormField
                              key={field}
                              control={newEhrForm.control}
                              name={field}
                              render={({ field, fieldState: { error } }) => (
                                <FormItem>
                                  <FormLabel>
                                    <Stack gap={2} direction="horizontal">
                                      {field.name}
                                      {dynamicSchema.shape &&
                                        dynamicSchema.shape[field.name]
                                          ?.description && (
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Icon
                                                name="HelpCircle"
                                                size="sm"
                                              />
                                            </TooltipTrigger>
                                            <TooltipContent
                                              align="start"
                                              alignOffset={-50}
                                              className="max-w-60 border-none bg-white text-wrap text-text-secondary shadow-md"
                                            >
                                              {dynamicSchema.shape[field.name]
                                                ?.description ?? ''}
                                            </TooltipContent>
                                          </Tooltip>
                                        )}
                                    </Stack>
                                  </FormLabel>
                                  <Input
                                    placeholder={t(
                                      'tenantAdmin.newEhr.enterField',
                                      { field: field.name },
                                    )}
                                    {...field}
                                  />
                                  {error && (
                                    <FormMessage>{error.message}</FormMessage>
                                  )}
                                </FormItem>
                              )}
                            />
                          ))}
                        </>
                      )}
                    </>
                  )}
                  <Stack gap={2} direction="horizontal">
                    <Button
                      disabled={!newEhrForm.formState.isDirty || isCreating}
                      className="flex-1"
                      type="submit"
                    >
                      {t('tenantAdmin.newEhr.addNewEhr')}
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => onOpenChange(false)}
                      className="flex-1"
                    >
                      {t('common.cancel')}
                    </Button>
                  </Stack>
                </Stack>
              </form>
            </Form>
          </Stack>
        </DialogContent>
      </Dialog>
    </div>
  );
};
